import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, BadgeCheck, Brain } from "lucide-react";
import { SITE, IMAGES } from "../config/site";

const LINES = [
  <>Emotionally</>,
  <>
    <em className="font-medium text-[#059669]">aware</em> childhoods
  </>,
  <>begin here.</>,
];

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-2.5, 3.5]);

  return (
    <section id="top" data-testid="hero-section" ref={ref} className="grain relative overflow-hidden bg-[#FDFBF7] pt-32 pb-16 sm:pt-40 lg:pb-24">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-[#ECFDF5]" />
      <div className="pointer-events-none absolute top-64 -left-52 h-[26rem] w-[26rem] rounded-full bg-[#F7F4EB]" />
      <div className="relative z-[2] mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <motion.p {...fade(0.05)} className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-[#059669]">
            Counselling Psychologist &middot; Children &middot; Teens &middot; Adults &amp; Families
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl font-medium leading-[1.04] tracking-tight text-[#064E3B] sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1.5">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p {...fade(0.7)} className="mt-7 max-w-xl text-base leading-relaxed text-[#526560] sm:text-lg">
            Professional counselling for every age — play therapy for children, and client-centred
            therapy, CBT and conflict management for teens, adults, parents and families. A calm,
            safe space to grow, whatever you carry in.
          </motion.p>
          <motion.div {...fade(0.85)} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#book"
              data-testid="hero-book-cta-button"
              className="group flex items-center gap-2 rounded-full bg-[#064E3B] px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:bg-[#022C22] hover:shadow-[0_14px_36px_-12px_rgba(6,78,59,0.55)]"
            >
              Book your first session
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services-button"
              className="group flex items-center gap-2 rounded-full border border-[#064E3B]/25 px-7 py-3.5 text-sm font-semibold text-[#064E3B] transition-colors duration-300 hover:bg-[#ECFDF5]"
            >
              Explore services
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
          <motion.div {...fade(1)} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-[#526560]">
            <span className="flex items-center gap-1.5">
              <BadgeCheck size={15} className="text-[#059669]" /> Verified at Mantra Care
            </span>
            <span className="flex items-center gap-1.5">
              <Brain size={15} className="text-[#D97706]" /> CBT &middot; Client-centred &middot; Play therapy
            </span>
            <span>Online &amp; in-person sessions</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yImg, rotate }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-[2rem] border border-[#064E3B]/10 shadow-[0_30px_60px_-30px_rgba(6,78,59,0.4)]"
            >
              <img
                src={IMAGES.hero}
                alt="A warm, professional counselling session welcoming all ages"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
            </motion.div>
            <motion.a
              href={SITE.mantraCareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-mantra-verified-card"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="glass-light absolute -left-4 top-8 flex items-center gap-3 rounded-2xl px-4 py-3 shadow-[0_10px_30px_-10px_rgba(6,78,59,0.25)] sm:-left-8"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#064E3B] text-[#FDFBF7]">
                <BadgeCheck size={18} />
              </span>
              <span>
                <span className="block text-xs font-bold text-[#064E3B]">Verified Psychologist</span>
                <span className="block text-[11px] text-[#526560]">at Mantra Care</span>
              </span>
            </motion.a>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="glass-light absolute -bottom-5 right-4 rounded-2xl px-5 py-3.5 shadow-[0_10px_30px_-10px_rgba(6,78,59,0.25)]"
              data-testid="hero-price-chip"
            >
              <span className="block text-[11px] uppercase tracking-[0.15em] text-[#526560]">Sessions from</span>
              <span className="font-serif text-2xl font-semibold text-[#064E3B]">{SITE.price}</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
